/**
 * Resolves the rows an ArTable actually shows: every row the table holds, narrowed
 * by the active split, the per-column filters and the visibility view's kit/group
 * member rules, then widened by `admitBundleHeaders` so a member that survives the
 * narrowing brings its own header (SYD-2406) — the grid does the same, and the
 * sheet has to agree with it row for row. The ticked selection is deliberately NOT
 * applied, and must stay unadmitted: classifying a selection subset orphans members
 * whose header is unticked, which is how the export fails closed (SYD-2279). Past
 * that, an orphan can only come from a header on another page.
 *
 * Consumed by `useArTableExport`, which narrows to the selection afterwards, and by
 * `useArTableWorkflowRowScope`, which sends the ids as `rowScope.visibleRowIds` so a
 * run cannot touch a row the user cannot see (SYD-2322). One resolver means the sheet
 * and the workflow can never disagree about what "visible" means.
 *
 * `isPaginated` picks the row source: past page one the store holds only the current
 * page, so every row comes from the `exportArTableRows` CF. That CF stops at
 * `AR_TABLE_MAX_ROWS` and says so nowhere in its response, so a short return against
 * `totalRows` sets `isTruncated` — the ids are then a subset and a caller scoping a
 * run by them must refuse. Neither source seeds from `uiState.renderedRowIds` — that
 * leaf is page-scoped and has collapsed bundles removed, and collapse is a viewport
 * affordance, not visibility.
 *
 * Which rows survive depends on the table's own `columns`, never on a caller's
 * `columnConfig`: `ArTableBody` resolves the split over the schema columns, so a
 * layout that omits the split column from its config must not thereby widen the row
 * set. `suppressionColumns` is the caller's narrowed set and reaches nothing but
 * `isCellSuppressed`.
 */

import { snapshot } from 'valtio';
import type { Snapshot } from 'valtio';
import { AR_TABLE_AGGREGATIONS_ROW_ID } from '#types/System.Artifacts.types';
import type {
  ArBundleType,
  ArColumnDefinition,
  ArRowData,
  ArRowId,
  ArTableId,
  TypeId,
} from '#types/System.Artifacts.types';
import type { SplitQuery, VisibilityType, VisibilityTypeId } from '#types/System.types';
import type {
  CollectionState,
  DocEntry,
  DocOrCollectionState,
  OrderByOption,
} from '@/store/Store.types';
import { exportArTableRows as exportArTableRowsCF } from '@/services/arTableFunctions';
import { resolveSplitForTable } from '../split/resolveSplitForTable';
import { filterArRowIdsBySplit } from '../split/filterArRowIdsBySplit';
import { filterArRowIdsByColumnFilters } from '../filter/filterArRowIdsByColumnFilters';
import { admitBundleHeaders } from '../bundleDisplay';
import { resolveArTableBundleExportVisibility } from '../export/resolveArTableBundleExportVisibility';

/** Every row the table holds, in render order, before any visibility gate runs. */
type ArTableRowSource = {
  rowsMap: Map<string, DocEntry<ArRowData>>;
  rowIds: ArRowId[];
};

type ArTableVisibleRows = {
  /** Row data for the whole table, not just the visible ids — formulas read across rows. */
  rowsMap: Map<string, DocEntry<ArRowData>>;
  visibleRowIds: ArRowId[];
  isCellSuppressed: (rowId: ArRowId, columnId: string) => boolean;
  bundleTypeByHeaderRowId: ReadonlyMap<ArRowId, ArBundleType>;
  /**
   * The row source came back short of `totalRows`, so `visibleRowIds` names only
   * part of the table. Any consumer that treats the ids as the complete set — a
   * workflow's row scope — must refuse rather than act on the remainder blindly.
   */
  isTruncated: boolean;
};

type ResolveArTableVisibleRowsParams = {
  tableId: ArTableId;
  parentPath: string;
  rowsOrderBy: OrderByOption;
  /** `uiState.pagination.totalPages > 1`; the caller owns the snapshot. */
  isPaginated: boolean;
  /**
   * `uiState.pagination.totalRows` — the server count, already net of the
   * aggregations row, as the `exportArTableRows` CF's own output is. The only way
   * the client can tell that the CF stopped at `AR_TABLE_MAX_ROWS`: it returns
   * `{ rows }` with no truncation signal of its own.
   */
  totalRows: number;
  /** The `rows` store state. Read once with `snapshot()`, never subscribed. */
  rowsState: DocOrCollectionState;
  /** The table type's own columns, unnarrowed — the split is resolved over these. */
  columns: readonly ArColumnDefinition[];
  /** The caller's rendered column set; consulted only by `isCellSuppressed`. */
  suppressionColumns?: readonly ArColumnDefinition[];
  splitQuery: SplitQuery | undefined;
  filterEntries: ReadonlyArray<readonly [TypeId, string]>;
  visibilityMode: VisibilityTypeId | undefined;
  visibilityType: VisibilityType | undefined;
  kitsEnabled: boolean;
  groupsEnabled: boolean;
};

const readStoreRows = (rowsState: DocOrCollectionState): ArTableRowSource => {
  const rowsSnap = snapshot(rowsState) as Snapshot<CollectionState<ArRowData>>;
  return {
    rowsMap: rowsSnap.docs,
    rowIds: (rowsSnap.docIds as ArRowId[]).filter((id) => id !== AR_TABLE_AGGREGATIONS_ROW_ID),
  };
};

const fetchAllRows = async (params: {
  parentPath: string;
  tableId: ArTableId;
  rowsOrderBy: OrderByOption;
}): Promise<ArTableRowSource> => {
  const cfResponse = await exportArTableRowsCF({
    parentPath: params.parentPath,
    tableId: params.tableId,
    orderBy: { field: params.rowsOrderBy.field, direction: params.rowsOrderBy.direction },
  });

  return {
    rowsMap: new Map<string, DocEntry<ArRowData>>(
      cfResponse.rows.map((row) => [row.docId, { data: row.data }]),
    ),
    rowIds: cfResponse.rows.map((row) => row.docId as ArRowId),
  };
};

async function resolveArTableVisibleRows(
  params: ResolveArTableVisibleRowsParams,
): Promise<ArTableVisibleRows> {
  const { rowsMap, rowIds } = params.isPaginated
    ? await fetchAllRows(params)
    : readStoreRows(params.rowsState);

  /**
   * Only the CF path can come up short: the store path reads the same documents
   * the grid renders from, so it cannot hold fewer rows than the grid shows. A
   * count of zero means the count query never landed, and a comparison against it
   * would refuse every run on a table whose pagination has not reported yet.
   */
  const isTruncated = params.isPaginated && params.totalRows > 0 && rowIds.length < params.totalRows;

  const split = resolveSplitForTable({ columns: params.columns, splitQuery: params.splitQuery });
  const rowIdsAfterSplit = filterArRowIdsBySplit({ rowIds, rowsDocs: rowsMap, split });

  const rowIdsAfterFilters = filterArRowIdsByColumnFilters({
    rowIds: rowIdsAfterSplit,
    rowsDocs: rowsMap,
    filterEntries: params.filterEntries,
  });

  /**
   * A header follows its members, exactly as the on-screen `ArTableBody` does: a
   * bundle keeping one member through the split or the filters re-admits its
   * header, so the sheet never shows a headerless member (SYD-2406). Run before
   * the classification below, which resolves each member's bundle type from the
   * header inside this very row set. Gated on the two bundle flags for the same
   * reason the grid gates it: with bundles off, rows keep their persisted
   * `bundleRole` but nothing draws a bundle, so a re-admitted header would be a
   * plain row the narrowing had excluded.
   */
  const rowIdsWithBundleHeaders = admitBundleHeaders({
    rowIds,
    admittedRowIds: rowIdsAfterFilters,
    rowsDocs: params.kitsEnabled || params.groupsEnabled ? rowsMap : null,
  });

  /**
   * Classify the split- and filter-narrowed rows, exactly as the on-screen
   * `ArTableBody` does. The header map comes from that same pass, so no row can
   * be both.
   */
  const bundleVisibility = resolveArTableBundleExportVisibility({
    rowIds: rowIdsWithBundleHeaders,
    rowsDocs: rowsMap,
    columns: params.suppressionColumns ?? params.columns,
    visibilityMode: params.visibilityMode,
    visibilityType: params.visibilityType,
    kitsEnabled: params.kitsEnabled,
    groupsEnabled: params.groupsEnabled,
  });

  return {
    rowsMap,
    visibleRowIds: bundleVisibility.visibleRowIds,
    isCellSuppressed: bundleVisibility.isCellSuppressed,
    bundleTypeByHeaderRowId: bundleVisibility.bundleTypeByHeaderRowId,
    isTruncated,
  };
}

export { resolveArTableVisibleRows };
export type { ArTableVisibleRows, ResolveArTableVisibleRowsParams };
